import { memo, useCallback } from 'react';
import { useSelector } from '@xstate/store/react';
import {
  useDraggable,
  useDroppable,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
} from '@dnd-kit/core';
import {
  Boxes, FileText, Type, Square, Shapes, Image, RectangleHorizontal, CircleDot,
  ChevronRight, ChevronDown as ChevronDownIcon,
} from 'lucide-react';
import { useCanvasStores } from '../../stores/CanvasContext';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { useLockedLayerIds } from '../../hooks/useLockedLayerIds';
import { encodeDragId, type LayerTreeItem } from './types';

// ============================================
// Pre-computed icons (avoid creating JSX on every render)
// ============================================

const ICON_FOR_KIND: Record<LayerTreeItem['kind'], React.ReactNode> = {
  node: <Square size={10} className="text-blue-400 shrink-0" />,
  group: <Boxes size={10} className="text-purple-400 shrink-0" />,
  textblock: <FileText size={10} className="text-amber-400 shrink-0" />,
  textlabel: <Type size={10} className="text-emerald-400 shrink-0" />,
  sprite: <Shapes size={10} className="text-violet-400 shrink-0" />,
  image: <Image size={10} className="text-sky-400 shrink-0" />,
  blocknode: <FileText size={10} className="text-purple-400 shrink-0" />,
  shape: <RectangleHorizontal size={10} className="text-indigo-400 shrink-0" />,
  'adr-trigger': <CircleDot size={10} className="text-red-400 shrink-0" />,
};

// ============================================
// Droppable Group Zone
// ============================================

export const DroppableGroupZone = memo(function DroppableGroupZone({ groupId, children }: { groupId: string; children: React.ReactNode }) {
  const dropId = `dropgroup:${groupId}`;
  const { setNodeRef, isOver } = useDroppable({ id: dropId });

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[4px] ${isOver ? 'bg-blue-50 ring-1 ring-blue-200 rounded' : ''}`}
    >
      {children}
    </div>
  );
});

// ============================================
// Draggable Tree Item Row (DnD wrapper)
// ============================================

export const DraggableItemRow = memo(function DraggableItemRow({
  item,
  depth,
  onItemContextMenu,
}: {
  item: LayerTreeItem;
  depth: number;
  onItemContextMenu: (entityId: string, e: React.MouseEvent) => void;
}) {
  const dragId = encodeDragId(item.kind, item.id);
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({ id: dragId });
  const dropId = `reorder:${item.kind}:${item.id}`;
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: dropId });

  return (
    <DraggableItemRowContent
      item={item}
      depth={depth}
      isDragging={isDragging}
      isOver={isOver}
      attributes={attributes}
      listeners={listeners}
      setDragRef={setDragRef}
      setDropRef={setDropRef}
      onItemContextMenu={onItemContextMenu}
    />
  );
});

// ============================================
// Memoized content (only re-renders when isDragging/isOver change)
// ============================================

const DraggableItemRowContent = memo(function DraggableItemRowContent({
  item,
  depth,
  isDragging,
  isOver,
  attributes,
  listeners,
  setDragRef,
  setDropRef,
  onItemContextMenu,
}: {
  item: LayerTreeItem;
  depth: number;
  isDragging: boolean;
  isOver: boolean;
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
  setDragRef: (el: HTMLElement | null) => void;
  setDropRef: (el: HTMLElement | null) => void;
  onItemContextMenu: (entityId: string, e: React.MouseEvent) => void;
}) {
  const { uiStore } = useCanvasStores();
  const isGroup = item.kind === 'group';
  // Always call the hook (Rules of Hooks) — only use the result when isGroup
  const isExpandedInStore = useSelector(uiStore, s => s.context.layerPanelExpandedGroups.has(item.id));
  const isExpanded = isGroup && isExpandedInStore;
  const isSelected = useSelector(canvasSelectionStore, s => s.context.selectedEntityIds.includes(item.id));
  const lockedLayerIds = useLockedLayerIds();
  const isItemLocked = lockedLayerIds.has(item.layerId);

  const setRefs = useCallback((el: HTMLElement | null) => {
    setDragRef(el);
    setDropRef(el);
  }, [setDragRef, setDropRef]);

  const handleToggleExpanded = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    uiStore.send({ type: 'layerPanel.toggleGroup', groupId: item.id });
  }, [uiStore, item.id]);

  const handleSelect = useCallback((e: React.MouseEvent) => {
    if (isItemLocked) return;
    if (e.shiftKey) {
      canvasSelectionStore.send({ type: 'entity.toggle', entityId: item.id });
    } else {
      canvasSelectionStore.send({ type: 'entity.selectOne', entityId: item.id });
    }
  }, [item.id, isItemLocked]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    if (item.kind !== 'adr-trigger' || !item.adrIds?.length) return;
    e.stopPropagation();
    uiStore.send({ type: 'adrDetail.open', adrId: item.adrIds[0] });
  }, [item.kind, item.adrIds, uiStore]);

  return (
    <>
      <div
        ref={setRefs}
        {...listeners}
        {...attributes}
        className={`flex items-center gap-1 py-0.5 pr-1 text-[11px] cursor-grab select-none ${isSelected ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'} ${isDragging ? 'opacity-40' : ''} ${isOver ? 'border-t-2 border-blue-400' : ''}`}
        style={{ paddingLeft: 8 + depth * 12 }}
        onClick={handleSelect}
        onDoubleClick={handleDoubleClick}
        onContextMenu={(e) => onItemContextMenu(item.id, e)}
      >
        {isGroup ? (
          <button
            className="p-0 text-gray-400 hover:text-gray-600 shrink-0"
            onClick={handleToggleExpanded}
          >
            {isExpanded ? <ChevronDownIcon size={10} /> : <ChevronRight size={10} />}
          </button>
        ) : (
          <span className="w-[10px] shrink-0" />
        )}

        {ICON_FOR_KIND[item.kind]}
        <span className="flex-1 truncate min-w-0">{item.label}</span>
      </div>

      {isGroup && isExpanded && item.children && (
        <DroppableGroupZone groupId={item.id}>
          {item.children.map(child => (
            <DraggableItemRow
              key={`${child.kind}-${child.id}`}
              item={child}
              depth={depth + 1}
              onItemContextMenu={onItemContextMenu}
            />
          ))}
          {item.children.length === 0 && (
            <div className="text-[10px] text-gray-300 italic py-0.5" style={{ paddingLeft: 8 + (depth + 1) * 12 }}>
              (leer)
            </div>
          )}
        </DroppableGroupZone>
      )}
    </>
  );
});
